import { useEffect, useRef, useState } from 'react';
import { useLiveEventData } from '../hooks/useLiveEventData';
import { getWindowObject } from '@eventbrite/feature-detection';
import { defineMessage, MessageDescriptor, FormattedMessage } from 'react-intl';
import { LoadingButton } from '@eventbrite/marmalade';
import { VERTICAL } from '@/types';
import styles from './CheckoutButton.module.scss';
import { TicketInformationResponse } from '@/pages/api/[eventId]/ticket-information.page';
import { recordComponentRenderTime } from '../../../../common/monitoring/rum-metrics';
import { useCheckoutLifecyclePostMessage } from './useCheckoutLifecyclePostMessage';
import { GACodes, LISTING_HEAP_IDS, DIRECT_AFF_CODE } from '@/components/_shared/constants';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { useAppContext } from '@/components/_shared/context/AppContext';
import { useIsPreview } from '@/components/_shared/contexts/PreviewContext';
import { QUERY_PARAMS } from '../../../../common/constants';
import { EBWidgetsScript } from '@/components/_shared/EBWidgetsScript/EBWidgetsScript';
import { getStableIdFromCookie } from '@/utils/widgetUtils';
import escape from 'lodash/escape';

const MESSAGES: Record<string, MessageDescriptor> = {
  RESERVE_SPOT: defineMessage({
    id: 'Reserve a spot',
    defaultMessage: 'Reserve a spot',
    description: 'Free Checkout button CTA',
  }),
  GET_TICKETS: defineMessage({
    id: 'Get tickets',
    defaultMessage: 'Get tickets',
    description: 'Checkout button CTA',
  }),
  REMIND_ME: defineMessage({
    id: 'Details',
    defaultMessage: 'Details',
    description: 'Remind me button CTA',
  }),
  SERIES: defineMessage({
    id: 'Check availability',
    defaultMessage: 'Check availability',
    description: 'Series button CTA',
  }),
  JOIN_WAITLIST: defineMessage({
    id: 'Join Waitlist',
    defaultMessage: 'Join Waitlist',
    description: 'Waitlist button CTA',
  }),
  RSVP_CONFIRM: defineMessage({
    id: 'Yes',
    defaultMessage: 'Yes',
    description: 'RSVP Yes button CTA',
  }),
};

export const GET_TICKETS_MESSAGE = MESSAGES.GET_TICKETS;

const getCheckoutButtonLabel = (
  isRemindMe?: boolean,
  isSeries?: boolean,
  ticketInfo?: TicketInformationResponse | null,
  vertical?: string,
  isRSVP?: boolean,
  hasWaitlistToken?: boolean,
) => {
  if (isRemindMe) {
    return MESSAGES.REMIND_ME;
  }
  if (isRSVP) {
    return MESSAGES.RSVP_CONFIRM;
  }
  if (isSeries) {
    return MESSAGES.SERIES;
  }
  if (!hasWaitlistToken && (ticketInfo?.availability?.waitlistAvailable || ticketInfo?.availability?.waitlistEnabled)) {
    return MESSAGES.JOIN_WAITLIST;
  }
  return ticketInfo?.isFree || vertical === VERTICAL.REGISTRATION ? MESSAGES.RESERVE_SPOT : MESSAGES.GET_TICKETS;
};

const getCheckoutButtonElementId = (eventId?: string, isRemindMe?: boolean, isRSVP?: boolean) => {
  const action = isRSVP ? 'rsvp' : isRemindMe ? 'details' : 'checkout';
  return `eventbrite-widget-modal-trigger-${eventId || 'event'}-${action}`;
};

export const CheckoutButton: React.FC<{ isRemindMe?: boolean; isRSVP?: boolean }> = ({
  isRemindMe = false,
  isRSVP = false,
}) => {
  const isPreview = useIsPreview();
  const {
    data: { serverUrl, stage },
  } = useAppContext();
  const { ticketInfo, loading, eventId, handleGetTicketsClick, isSeries, vertical, seriesId } = useLiveEventData();

  const modalTriggerElementId = getCheckoutButtonElementId(eventId, isRemindMe, isRSVP);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const checkoutInteractionStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPreview || !isScriptLoaded || loading) return;

    requestAnimationFrame(() => {
      const buttonElement = document.getElementById(modalTriggerElementId);
      const ebWidgets = getWindowObject('EBWidgets');

      if (!buttonElement || !ebWidgets) {
        return;
      }

      const searchParams = new URLSearchParams(getWindowObject('location')?.search ?? '');
      const affiliateCode = searchParams.get(QUERY_PARAMS.AFFILIATE_CODE) ?? DIRECT_AFF_CODE;
      const waitlistToken = searchParams.get(QUERY_PARAMS.WAITLIST_TOKEN);
      const rsvpToken = searchParams.get(QUERY_PARAMS.MIPA);
      const stableId = getStableIdFromCookie();
      const promoCode = ticketInfo?.appliedPromoCode || ticketInfo?.discounts?.[0]?.code;

      ebWidgets.createWidget({
        widgetType: 'checkout',
        eventId: waitlistToken ? eventId : isSeries ? seriesId : eventId,
        modal: true,
        modalTriggerElementId,
        affiliateCode,
        ...(promoCode ? { promoCode: escape(promoCode) } : {}),
        ...(waitlistToken ? { waitlistToken } : {}),
        ...(stableId ? { extraParams: [{ name: 'stableID', value: stableId }] } : {}),
        ...(rsvpToken ? { rsvpToken } : {}),
      });

      const hasTicketsHash = getWindowObject('location')?.hash === '#tickets';
      if (hasTicketsHash || waitlistToken) {
        buttonElement.click();
      }
    });
  }, [isPreview, eventId, modalTriggerElementId, isScriptLoaded, loading, ticketInfo, seriesId, isSeries]);

  useEffect(() => {
    if (modalTriggerElementId && !loading) {
      recordComponentRenderTime('CheckoutButton');
    }
  }, [modalTriggerElementId, loading]);

  useCheckoutLifecyclePostMessage({
    serverUrl,
    stage,
    eventId,
    enabled: !isPreview && Boolean(modalTriggerElementId) && isScriptLoaded && !loading,
    interactionStartedAtRef: checkoutInteractionStartedAtRef,
  });

  const hasWaitlistToken =
    typeof window !== 'undefined'
      ? Boolean(new URLSearchParams(getWindowObject('location')?.search ?? '').get(QUERY_PARAMS.WAITLIST_TOKEN)?.trim())
      : false;

  const buttonLabel = getCheckoutButtonLabel(
    isRemindMe,
    isSeries ?? false,
    ticketInfo,
    vertical,
    isRSVP,
    hasWaitlistToken,
  );

  if (isPreview) {
    return (
      <LoadingButton
        isLoading={loading}
        id="preview-checkout-button-disabled"
        data-testid="conversion-bar-checkout-button"
        role="button"
        variant={isRemindMe ? 'secondary' : 'primary'}
        className={styles.button}
        disabled
      >
        <FormattedMessage {...buttonLabel} />
      </LoadingButton>
    );
  }

  const handleButtonClick = () => {
    checkoutInteractionStartedAtRef.current = performance.now();
    if (isRSVP) {
      trackAnalyticsEvent({
        category: GACodes.GA_CATEGORY_LISTING,
        action: GACodes.GA_ACTION_RSVP_YES_INTENT,
      });
    }
    handleGetTicketsClick(buttonLabel.id);
  };

  return (
    <>
      <EBWidgetsScript hostname={serverUrl} onScriptLoad={() => setIsScriptLoaded(true)} stage={stage} />
      <LoadingButton
        isLoading={loading}
        id={modalTriggerElementId}
        role="button"
        variant={isRemindMe ? 'secondary' : 'primary'}
        className={styles.button}
        data-testid="conversion-bar-checkout-button"
        data-heap-id={LISTING_HEAP_IDS.CHECKOUT_BUTTON}
        onClick={handleButtonClick}
      >
        <FormattedMessage {...buttonLabel} />
      </LoadingButton>
    </>
  );
};
